import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher, useLoaderData } from 'react-router';

import { LOAD_MORE_FEATURED_PROJECTS_ACTION } from '~/routes/actions.load-more-featured-projects';
import { FEATURED_PROJECTS_PAGE_SIZE } from '~/modules/product/util/featured-projects-config';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared';
import { InTheWildRun } from './InTheWildRun';
import type { WildPlate } from './InTheWildPlate';
import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared/LoadMoreButton';
import { SectionHead } from '~/ui-rocco/pages/detail-shared/SectionHead';

/** One composition's worth of projects, and the step each Load More adds. */
const RUN_SIZE = 4;

/**
 * (05) IN THE WILD — the projects this product has been specified in, in the
 * staggered composition from Figma 149:13325 rather than as a grid of equal
 * tiles: four to a run, and another run for every Load More.
 *
 * The loader SSRs the first page of tagged projects and
 * {@link LOAD_MORE_FEATURED_PROJECTS_ACTION} appends the rest, so the reveal
 * window is topped up from the server before it runs the local pool dry.
 * Projects without a photograph are dropped — a plate is the composition.
 */
export const InTheWildSection = () => {
    const {
        product,
        featuredProjects: { FeaturedProjects },
    } = useLoaderData<typeof loader>();

    type Tile = (typeof FeaturedProjects.edges)[number]['node'];

    const [visibleCount, setVisibleCount] = useState(RUN_SIZE);
    const fetcher = useFetcher<{
        projects: Tile[];
        hasMore: boolean;
        totalCount: number;
    }>();
    const [appended, setAppended] = useState<Tile[]>([]);
    const [serverHasMore, setServerHasMore] = useState<boolean | null>(null);
    const nextOffsetRef = useRef(FEATURED_PROJECTS_PAGE_SIZE);
    const requestedOffsetRef = useRef(-1);
    const processedOffsetRef = useRef(-1);

    // Navigating to another product reuses this instance (same route), so the
    // state survives. Reset during render — an effect lands a paint late and
    // flashes the previous product's projects into the new run.
    const [renderedLink, setRenderedLink] = useState(product.Link);
    if (renderedLink !== product.Link) {
        setRenderedLink(product.Link);
        setVisibleCount(RUN_SIZE);
        setAppended([]);
        setServerHasMore(null);
        nextOffsetRef.current = FEATURED_PROJECTS_PAGE_SIZE;
        requestedOffsetRef.current = -1;
        processedOffsetRef.current = -1;
    }

    const initialTiles = useMemo(
        () => FeaturedProjects.edges.map((edge) => edge.node),
        [FeaturedProjects],
    );

    const fetchPage = useCallback(
        (offset: number) => {
            if (requestedOffsetRef.current === offset) return;
            requestedOffsetRef.current = offset;
            const params = new URLSearchParams({
                link: product.Link,
                offset: String(offset),
                limit: String(FEATURED_PROJECTS_PAGE_SIZE),
            });
            fetcher.load(
                `${LOAD_MORE_FEATURED_PROJECTS_ACTION}?${params.toString()}`,
            );
        },
        [fetcher, product.Link],
    );

    // Append each fetched page once. An empty payload means the action
    // rejected the request or hit a 500 — clear the in-flight guard so the
    // same offset can be retried.
    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) return;
        if (processedOffsetRef.current === requestedOffsetRef.current) return;

        const incoming = fetcher.data.projects ?? [];
        if (incoming.length === 0) {
            requestedOffsetRef.current = -1;
            return;
        }

        processedOffsetRef.current = requestedOffsetRef.current;
        setAppended((prev) => {
            const seen = new Set(
                [...initialTiles, ...prev].map((tile) => tile.id),
            );
            const fresh = incoming.filter((tile) => !seen.has(tile.id));
            return fresh.length ? [...prev, ...fresh] : prev;
        });
        setServerHasMore(Boolean(fetcher.data.hasMore));
        nextOffsetRef.current =
            requestedOffsetRef.current + FEATURED_PROJECTS_PAGE_SIZE;
    }, [fetcher.state, fetcher.data, initialTiles]);

    const plates = useMemo<WildPlate[]>(
        () =>
            [...initialTiles, ...appended]
                .filter((tile) => tile.images?.[0]?.src)
                .map((tile) => ({
                    id: tile.id,
                    title: tile.title,
                    link: tile.link,
                    src: tile.images[0]?.src ?? '',
                    architect: tile.professional?.name ?? '',
                    architectId: tile.professional?.id,
                    architectLink: tile.professional?.link,
                    website: tile.website ?? undefined,
                })),
        [initialTiles, appended],
    );

    const hasMoreOnServer =
        serverHasMore ?? FeaturedProjects.pageInfo.hasNextPage;

    const onLoadMore = () => {
        const nextVisible = visibleCount + RUN_SIZE;
        // Top up before the window runs the pool dry, so the next tap reveals
        // instantly instead of waiting on a fetch.
        if (
            plates.length < nextVisible + RUN_SIZE &&
            hasMoreOnServer &&
            fetcher.state === 'idle'
        ) {
            fetchPage(nextOffsetRef.current);
        }
        setVisibleCount(nextVisible);
    };

    if (plates.length === 0) return null;

    const shown = plates.slice(0, visibleCount);
    // One run per four projects, so the composition repeats down the page.
    const runs: WildPlate[][] = [];
    for (let index = 0; index < shown.length; index += RUN_SIZE) {
        runs.push(shown.slice(index, index + RUN_SIZE));
    }

    const showLoadMore = plates.length > visibleCount || hasMoreOnServer;
    // Only show the loading state when the tap is actually waiting on the
    // server; background top-ups stay silent.
    const isLoadingMore =
        fetcher.state !== 'idle' && plates.length <= visibleCount;

    return (
        <section className="bg-ground max-md:px-6 md:px-[3rem]">
            <div className={CONTENT_CAP_PADDED}>
                <SectionHead
                    lead="Specified in"
                    subject="these projects"
                    standfirst={`Where ${product.Title} has been used, and who designed the room it went into.`}
                />

                <div className="flex flex-col max-md:mt-8 max-md:gap-10 md:mt-[5.625rem] md:gap-[5.625rem]">
                    {runs.map((run, index) => (
                        <InTheWildRun key={run[0]?.id ?? index} plates={run} />
                    ))}
                </div>

                {showLoadMore ? (
                    <LoadMoreButton
                        className="max-md:mt-12 md:mt-[5.625rem]"
                        isLoading={isLoadingMore}
                        onClick={onLoadMore}
                    />
                ) : null}
            </div>
        </section>
    );
};
